import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type { Conversation, Message } from '@/types';

interface ChatContextType {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Message[];
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  isSending: boolean;
  error: string | null;

  loadConversations: () => Promise<void>;
  createConversation: () => Promise<Conversation>;
  selectConversation: (id: string) => Promise<void>;
  sendMessage: (content: string, conversationId?: string) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    setError(null);
    try {
      const data = await api.listConversations();
      setConversations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  const createConversation = useCallback(async () => {
    setError(null);
    try {
      const conversation = await api.createConversation();
      setConversations(prev => [conversation, ...prev]);
      setActiveConversation(conversation);
      setMessages([]);
      return conversation;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
      throw err;
    }
  }, []);

  const selectConversation = useCallback(async (id: string) => {
    setIsLoadingMessages(true);
    setError(null);
    try {
      const [conversation, msgs] = await Promise.all([
        api.getConversation(id),
        api.listMessages(id),
      ]);
      setActiveConversation(conversation);
      setMessages(msgs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const sendMessage = useCallback(async (content: string, conversationId?: string) => {
    const targetConversation = conversationId ? { id: conversationId } : activeConversation;
    if (!targetConversation) {
      setError('No active conversation');
      return;
    }

    setIsSending(true);
    setError(null);

    // Optimistic update - add user message immediately
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await api.sendMessage(targetConversation.id, content);

      // Replace temp message with real ones
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMessage.id),
        response.user_message,
        response.assistant_message,
      ]);

      // Update conversation in list
      setConversations(prev =>
        prev.map(c =>
          c.id === targetConversation.id
            ? {
                ...c,
                message_count: c.message_count + 2,
                last_message_at: response.assistant_message.created_at,
                title: c.title || content.slice(0, 50),
              }
            : c
        )
      );

      // Update active conversation
      setActiveConversation(prev =>
        prev
          ? {
              ...prev,
              message_count: prev.message_count + 2,
              last_message_at: response.assistant_message.created_at,
              title: prev.title || content.slice(0, 50),
            }
          : null
      );
    } catch (err) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [activeConversation]);

  const toggleFavorite = useCallback(async (id: string) => {
    const conversation = conversations.find(c => c.id === id);
    if (!conversation) return;

    try {
      const updated = await api.updateConversation(id, {
        is_favorite: !conversation.is_favorite,
      });
      setConversations(prev =>
        prev.map(c => (c.id === id ? updated : c))
      );
      if (activeConversation?.id === id) {
        setActiveConversation(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update conversation');
    }
  }, [conversations, activeConversation]);

  const deleteConversation = useCallback(async (id: string) => {
    try {
      await api.deleteConversation(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConversation?.id === id) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  }, [activeConversation]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        activeConversation,
        messages,
        isLoadingConversations,
        isLoadingMessages,
        isSending,
        error,
        loadConversations,
        createConversation,
        selectConversation,
        sendMessage,
        toggleFavorite,
        deleteConversation,
        clearError,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
